import React, { useState } from "react";
import styled from "styled-components";
import {
  IconButton,
  ButtonGroup,
  Popover,
  Whisper,
  Toggle,
  Panel,
  SelectPicker,
} from "rsuite";
import SettingIcon from "@rsuite/icons/Setting";
import TimeIcon from "@rsuite/icons/Time";
import VisibleIcon from "@rsuite/icons/Visible";
import UnvisibleIcon from "@rsuite/icons/Unvisible";
import { colors } from "../../../../ui";

const ChartControls = ({
  timeRange,
  onTimeRangeChange,
  chartLibrary,
  onChartLibraryChange,
  configControls,
  onToggleSelector,
  showSelector,
  availableUnitTypes,
  onUnitChange,
  className,
  ...props
}) => {
  const timeRangeOptions = [
    { value: "1h", label: "Last Hour" },
    { value: "24h", label: "Last 24 Hours" },
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "all", label: "All Data" },
  ];

  const chartLibraryOptions = [
    { value: "canvas", label: "CanvasJS" },
    { value: "fusion", label: "FusionCharts" },
  ];

  const timeRangeMenu = (
    <TimeRangeMenu>
      {timeRangeOptions.map((option) => (
        <TimeRangeMenuItem
          key={option.value}
          onClick={() => onTimeRangeChange(option.value)}
          active={timeRange === option.value}
        >
          {option.label}
        </TimeRangeMenuItem>
      ))}
    </TimeRangeMenu>
  );

  const settingsPanel = (
    <SettingsPanel>
      {configControls && (
        <>
          <SettingDivider />
          <SettingSection>
            <ToggleRow>
              <ToggleLabel>Show Grid</ToggleLabel>
              <Toggle
                checked={configControls.showGrid.value}
                onChange={configControls.showGrid.setValue}
                size="sm"
              />
            </ToggleRow>
          </SettingSection>

          <SettingSection>
            <ToggleRow>
              <ToggleLabel>Show Legend</ToggleLabel>
              <Toggle
                checked={configControls.showLegend.value}
                onChange={configControls.showLegend.setValue}
                size="sm"
              />
            </ToggleRow>
          </SettingSection>

          <SettingSection>
            <ToggleRow>
              <ToggleLabel>Animation</ToggleLabel>
              <Toggle
                checked={configControls.animationEnabled.value}
                onChange={configControls.animationEnabled.setValue}
                size="sm"
              />
            </ToggleRow>
          </SettingSection>
        </>
      )}
    </SettingsPanel>
  );

  return (
    <ControlsContainer className={className} {...props}>
      <StyledButtonGroup size="sm">
        {/* Toggle Data Selector */}
        <IconButton
          icon={showSelector ? <UnvisibleIcon /> : <VisibleIcon />}
          onClick={onToggleSelector}
          title={showSelector ? "Hide data selector" : "Show data selector"}
          appearance="subtle"
        />

        {/* Time Range Selector */}
        <Whisper
          trigger="click"
          placement="bottomStart"
          speaker={<Popover>{timeRangeMenu}</Popover>}
        >
          <IconButton
            icon={<TimeIcon />}
            title={`Time Range: ${timeRangeOptions.find((opt) => opt.value === timeRange)?.label}`}
            appearance="subtle"
          />
        </Whisper>

        {/* Chart Settings */}
        <Whisper
          trigger="click"
          placement="bottomEnd"
          speaker={<Popover title="Chart Settings">{settingsPanel}</Popover>}
        >
          <IconButton
            icon={<SettingIcon />}
            title="Chart settings"
            appearance="subtle"
          />
        </Whisper>
      </StyledButtonGroup>

      {/* Current Settings Display */}
      <SettingsDisplay>
        <SettingChip>
          {timeRangeOptions.find((opt) => opt.value === timeRange)?.label}
        </SettingChip>
        <SettingChip>
          {chartLibraryOptions.find((opt) => opt.value === chartLibrary)?.label}
        </SettingChip>
      </SettingsDisplay>
    </ControlsContainer>
  );
};

// Styled Components
const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
  }
`;

const StyledButtonGroup = styled(ButtonGroup)`
  .rs-btn {
    border-color: ${colors.neutral[300]};

    &:hover {
      border-color: ${colors.primary.main};
      color: ${colors.primary.main};
    }
  }
`;

const SettingsDisplay = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const SettingChip = styled.span`
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const SettingsPanel = styled(Panel)`
  width: 280px;
  padding: 16px;
  max-height: 600px;
  overflow-y: auto;
`;

const SettingSection = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.neutral[700]};
  margin-bottom: 6px;
`;

const SettingSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const SettingDivider = styled.div`
  height: 1px;
  background: ${colors.neutral[200]};
  margin: 16px 0;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToggleLabel = styled.div`
  font-size: 13px;
  color: ${colors.neutral[700]};
`;

const TimeRangeMenu = styled.div`
  min-width: 180px;
  padding: 8px 0;
`;

const TimeRangeMenuItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${colors.neutral[800]};
  font-weight: ${(props) => (props.active ? "600" : "normal")};

  &:hover {
    background: ${(props) =>
      props.active ? colors.primary.main + "15" : colors.neutral[100]};
  }

  transition: background 0.2s;
`;

export default ChartControls;
